import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminApplications() {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedApp, setSelectedApp] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  const fetchApplications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/seller-applications?status=${filter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setApplications(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (appId, approved) => {
    if (!approved && !rejectionReason.trim()) {
      toast.error('Lütfen red sebebi girin');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/seller-applications/${appId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          approved,
          rejection_reason: approved ? null : rejectionReason
        })
      });

      if (res.ok) {
        toast.success(approved ? 'Başvuru onaylandı!' : 'Başvuru reddedildi');
        setSelectedApp(null);
        setRejectionReason('');
        fetchApplications();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'İşlem başarısız');
      }
    } catch (err) {
      toast.error('Bir hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="flex items-center gap-1 text-yellow-600"><Clock className="w-4 h-4" /> Bekliyor</span>;
      case 'approved':
        return <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-4 h-4" /> Onaylandı</span>;
      case 'rejected':
        return <span className="flex items-center gap-1 text-red-600"><XCircle className="w-4 h-4" /> Reddedildi</span>;
      default:
        return status;
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Satıcı Başvuruları</h1>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['pending', 'approved', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === status
                ? 'bg-orange-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {status === 'pending' ? 'Bekleyen' : status === 'approved' ? 'Onaylanan' : 'Reddedilen'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      ) : applications.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">Bu kategoride başvuru yok</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Mağaza Adı</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Başvuran</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">İl</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Tarih</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Durum</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {applications.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{app.store_name}</td>
                  <td className="px-4 py-3">
                    <div>{app.user_name}</div>
                    <div className="text-sm text-gray-500">{app.user_email}</div>
                  </td>
                  <td className="px-4 py-3">{app.city}</td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(app.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(app.status)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedApp(app)}
                      className="flex items-center gap-1 text-orange-500 hover:text-orange-600"
                    >
                      <Eye className="w-4 h-4" /> Detay
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Başvuru Detayı</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Mağaza Adı</label>
                  <p className="font-medium">{selectedApp.store_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Başvuran</label>
                  <p className="font-medium">{selectedApp.user_name}</p>
                  <p className="text-sm text-gray-500">{selectedApp.user_email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Telefon</label>
                  <p className="font-medium">{selectedApp.phone}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">İl</label>
                  <p className="font-medium">{selectedApp.city}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Vergi No</label>
                  <p className="font-medium">{selectedApp.tax_number}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Vergi Dairesi</label>
                  <p className="font-medium">{selectedApp.tax_office}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-500">IBAN</label>
                  <p className="font-medium font-mono">{selectedApp.iban}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-500">Adres</label>
                  <p className="font-medium">{selectedApp.address}</p>
                </div>
                {selectedApp.store_description && (
                  <div className="col-span-2">
                    <label className="text-sm text-gray-500">Açıklama</label>
                    <p>{selectedApp.store_description}</p>
                  </div>
                )}
              </div>

              {selectedApp.status === 'pending' && (
                <div className="pt-4 border-t">
                  <label className="block text-sm font-medium mb-2">Red Sebebi (reddetmek için)</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="Reddedilecekse sebebini yazın..."
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setSelectedApp(null); setRejectionReason(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Kapat
              </button>
              {selectedApp.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleProcess(selectedApp.id, false)}
                    disabled={processing}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                  >
                    Reddet
                  </button>
                  <button
                    onClick={() => handleProcess(selectedApp.id, true)}
                    disabled={processing}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                  >
                    Onayla
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
